import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from "react";

/**
 * A thread as the marker that stands for it, wherever it is drawn — everything
 * a marker renders that isn't about *where* it sits.
 */
export type CommentThreadMarker = {
  threadId: string;
  authorName: string;
  /** The thread opens with an agent's reply, so it is a review rather than a remark. */
  isAgentThread: boolean;
  isResolved: boolean;
  isUnread: boolean;
};

/** The pin's box, in px. Shared: the cursor is drawn to the same dimensions. */
export const PIN_SIZE = 24;

// Unread fills the whole pin rather than ringing it: at 24px a ring reads as an
// artifact of the artwork behind it, where a solid colour switch doesn't.
export const UNREAD_PIN = {
  bg: "primary.base",
  hover: "primary.hover",
};

/**
 * The teardrop pin: fully round except for a sharp bottom-left corner, which is
 * the point that sits on the spot being commented on.
 *
 * The white ring is stacked box-shadows rather than a border, so it doesn't eat
 * into the 24px box — matching the design's focus-ring treatment.
 */
export const PIN_SHAPE = {
  borderTopLeftRadius: "full",
  borderTopRightRadius: "full",
  borderBottomRightRadius: "full",
  borderBottomLeftRadius: 0,
  bg: "teal.100",
  boxShadow: "0 0 0 1px var(--chakra-colors-base-white)",
} as const;

/** What a movable pin answers to, from the drag that moves it. */
export type PinDragHandlers = {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
};

/** How far a press may travel and still count as a click, not a gesture. */
export const CLICK_SLOP_PX = 4;

/** Whether a press has travelled far enough to be a drag rather than a click. */
export function isBeyondClickSlop(
  origin: { x: number; y: number },
  point: { x: number; y: number },
): boolean {
  return Math.hypot(point.x - origin.x, point.y - origin.y) > CLICK_SLOP_PX;
}
